/**
 * `Acquisition_Source` — the top-level channel a user arrived through, stamped
 * once at signup and read back when Mixpanel identifies them.
 *
 * It cannot be derived at identify time: `user.inviteAccepted` defaults to
 * `true` for everyone (see `authStore`), and the invite code itself is a
 * transient `pendingJoinCode` that the OAuth flow consumes and deletes. So the
 * signup paths — which *do* know whether a code was in play — record it here.
 */

export const ACQUISITION_SOURCE_KEY = "mash:acquisition_source";

/** Vocabulary fixed by the event-taxonomy design doc. */
export type AcquisitionSource = "Invite Link" | "Organic";

/**
 * Stamp the channel for this user. First write wins: a user who lands via an
 * invite link and only later signs in with Google is still invite-acquired.
 */
export function markAcquisitionSource(source: AcquisitionSource): void {
  try {
    if (localStorage.getItem(ACQUISITION_SOURCE_KEY)) return;
    localStorage.setItem(ACQUISITION_SOURCE_KEY, source);
  } catch {
    /* storage unavailable — the property just goes unset */
  }
}

/**
 * Read the stamped channel. Returns `null` rather than guessing "Organic" so
 * callers can distinguish "signed up organically" from "signed up before this
 * code shipped", and leave the property unset in the latter case.
 */
export function readAcquisitionSource(): AcquisitionSource | null {
  try {
    const raw = localStorage.getItem(ACQUISITION_SOURCE_KEY);
    return raw === "Invite Link" || raw === "Organic" ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Drop the stamp on sign-out. This is device-scoped storage but the value is
 * user-scoped: without this, the next account to sign in on the same device
 * inherits the previous user's acquisition channel.
 */
export function clearAcquisitionSource(): void {
  try {
    localStorage.removeItem(ACQUISITION_SOURCE_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
